import {Stack, Alert as AlertInfo} from '@mui/material';
import DateRangeIcon from '@mui/icons-material/DateRange';
import TodayIcon from '@mui/icons-material/Today';
import BusinessIcon from '@mui/icons-material/Business';
import {CagePanIcon, FishIcon} from 'styles/icons';
import useLanguage from 'hooks/useLanguage';
import {useTranslation} from 'react-i18next';

import {resolveProperty, mergeArrays, siteCageOptions} from './FishGroup/helpers';
import {FishGroupRow} from './FishGroup/FishGroupRow';
import {FishGroupName} from './FishGroup/FishGroupName';
import {FishGroupRowCertificates} from './FishGroup/FishGroupRowCertificates';
import {FishGroupRowDateRange} from './FishGroup/FishGroupRowDateRange';
import InputActionButtons from './InputActionButtons';
import {useFishGroups} from './FishGroup/useFishGroups';
import {CVField} from './FishGroup/FishGroupCV';
import {FishGroupModelAssignmentRow} from './FishGroup/FishGroupModelAssignmentRow';
import {FishGroupPropertiesRow} from './FishGroup/FishGroupPropertiesRow';
import {useFlags} from 'launchdarkly-react-client-sdk';
import {SectionHeader} from 'components/Generic/SectionHeader';

export const FishGroupMenu = () => {
  const {t} = useTranslation('common');
  const {dateFormatter} = useLanguage();
  const {state, dispatch, setUpdateFishGroups, updateFishGroups} = useFishGroups();
  const {fishGroupPropertiesEnabled, ownership: ownershipEnabled} = useFlags();

  return (
    <form noValidate autoComplete="off" style={{width: '100%'}}>
      <Stack direction="column" spacing={2}>
        <Stack direction="column" spacing={2} pb={2}>
          {state.fishGroupIds.length > 1 && (
            <AlertInfo variant="outlined" severity="info">
              {`${state.fishGroupIds.length} ${t('fishGroupsSelected')}`}
            </AlertInfo>
          )}
          <Stack direction="column" spacing={1} sx={{'&>*': {minHeight: 32}}}>
            <FishGroupName label={t('name')} state={state} dispatch={dispatch} />
            <FishGroupRow
              label={t('unit')}
              value={`${state.fishGroup?.site?.name} | ${state.fishGroup?.cage?.name}`}
              icon={<CagePanIcon fontSize="inherit" />}
              options={siteCageOptions(
                resolveProperty(state.fishGroups, 'site.name'),
                resolveProperty(state.fishGroups, 'cage.name'),
              )}
            />
            <FishGroupRow
              label={t('species')}
              value={state.fishGroup?.species ? t(state.fishGroup?.species) : ''}
              icon={<FishIcon fontSize="inherit" />}
              options={resolveProperty(state.fishGroups, 'species')}
            />
            {state.fishGroup?.certificates && Object.keys(state.fishGroup?.certificates).length !== 0 && (
              <FishGroupRowCertificates label={t('certificates')} value={state.fishGroup?.certificates ?? {}} />
            )}
            <FishGroupRow
              label={t('inputDate')}
              value={state.fishGroup?.inputDate ? dateFormatter.toLongDateString(state.fishGroup?.inputDate) : ''}
              icon={<TodayIcon fontSize="inherit" />}
              options={resolveProperty(state.fishGroups, 'inputDate')}
            />
            {ownershipEnabled && (
              <FishGroupRow
                label={t('owner')}
                value={state.fishGroup?.company?.name ?? ''}
                icon={<BusinessIcon fontSize="inherit" />}
                options={resolveProperty(state.fishGroups, 'company.name')}
              />
            )}
            <FishGroupRow
              label={t('fishType')}
              value={state.fishGroup?.fishType?.name ?? ''}
              icon={<FishIcon fontSize="inherit" />}
              options={resolveProperty(state.fishGroups, 'fishType.name')}
            />
            <FishGroupRowDateRange
              label={t('date')}
              startDate={state.fishGroup?.startDate ? dateFormatter.toLongDateString(state.fishGroup?.startDate) : ''}
              endDate={state.fishGroup?.endDate ? dateFormatter.toLongDateString(state.fishGroup?.endDate) : ''}
              icon={<DateRangeIcon fontSize="inherit" />}
              options={mergeArrays(
                resolveProperty(state.fishGroups, 'startDate'),
                resolveProperty(state.fishGroups, 'endDate'),
                '→',
              )}
            />
            {fishGroupPropertiesEnabled && <FishGroupPropertiesRow {...{state, dispatch}} />}

            <SectionHeader label={t('models')} />
            <CVField {...{state, dispatch}} />
            <FishGroupModelAssignmentRow {...{state, dispatch}} />
          </Stack>
        </Stack>
      </Stack>
      <InputActionButtons
        setCreateUpdateInput={setUpdateFishGroups}
        loading={updateFishGroups}
        disabled={updateFishGroups}
      />
    </form>
  );
};
